import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { usePositions } from "../hooks/usePositions";
import {
  formatPrice,
  formatSize,
  formatMargin,
  formatPnL,
} from "../sdk/utils/display";
import { fromSatoshi } from "../sdk/utils/math";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  LayoutHeader,
  TableHeaderCell,
  TableHeaderCellStatic,
  CloseButton,
} from "./smartlayout/fragments";
import {
  DEFAULT_BASE_URL,
  DEFAULT_HEIGHT_BUCKET_POSITIONS,
  DEFAULT_REFRESH_INTERVAL,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
  ORDER_SIDE_LONG,
  ORDER_SIDE_SHORT,
  MARGIN_MODE_CROSS,
  POSITION_SORT_FIELD_SYMBOL,
  POSITION_SORT_FIELD_LEVERAGE,
  POSITION_SORT_FIELD_SIZE,
  POSITION_SORT_FIELD_LIQUIDATION_PRICE,
  POSITION_SORT_FIELD_UNREALIZED_PNL,
  POSITION_SORT_FIELD_ENTRY_PRICE,
  POSITION_SORT_FIELD_MARGIN,
  POSITION_VIEW_MODE_ALL,
  POSITION_VIEW_MODE_BY_BUCKET,
  POSITION_GRID_TEMPLATE_COLUMNS,
  POSITION_PNL_COLOR_POSITIVE,
  POSITION_PNL_COLOR_NEGATIVE,
  POSITION_LOADING_MESSAGE,
  POSITION_EMPTY_MESSAGE,
  POSITION_TITLE_PREFIX,
  POSITION_TAB_LABEL_ALL,
  POSITION_TAB_LABEL_BY_BUCKET,
  POSITION_LEVERAGE_FALLBACK,
  PositionSortField,
  PositionSortDirection,
  PositionViewMode,
} from "./smartlayout/constants";
import "./smartlayout/layout.css";

interface BucketPositionsProps {
  baseURL?: string;
  authToken?: string;
  walletAddress?: string;
  height?: string;
  refreshInterval?: number;
  selectedBucketId?: string | null;
}

function BucketPositions({
  baseURL = DEFAULT_BASE_URL,
  authToken,
  walletAddress,
  height = DEFAULT_HEIGHT_BUCKET_POSITIONS,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
  selectedBucketId = null,
}: BucketPositionsProps): JSX.Element {
  const { positions, loading, error } = usePositions({
    baseURL,
    authToken,
    walletAddress,
    refreshInterval,
  });

  const [viewMode, setViewMode] = useState<PositionViewMode>(POSITION_VIEW_MODE_ALL);
  const [sortField, setSortField] = useState<PositionSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<PositionSortDirection>(SORT_DIRECTION_DESC);

  /**
   * Format leverage from satoshi to decimal with appropriate precision
   * Leverage is typically shown as whole numbers or 1-2 decimal places (e.g., "10x", "1.5x")
   */
  const formatLeverage = (leverage: number | string | undefined | null): string => {
    if (leverage === undefined || leverage === null) return POSITION_LEVERAGE_FALLBACK;
    
    try {
      // Convert from satoshi to decimal
      const decimal = typeof leverage === 'string' || typeof leverage === 'number' 
        ? fromSatoshi(leverage) 
        : 0;
      
      // Format with up to 2 decimal places, remove trailing zeros
      return decimal.toFixed(2).replace(/\.?0+$/, "");
    } catch (error) {
      return POSITION_LEVERAGE_FALLBACK;
    }
  };

  // Filter and sort positions
  const processedPositions = useMemo(() => {
    let filtered = [...positions];

    // Filter by selected bucket if provided
    if (selectedBucketId) {
      filtered = filtered.filter((pos) => {
        // Convert both to string for comparison to handle number/string mismatches
        const posBucketId = pos.bucketId?.toString();
        const selectedId = selectedBucketId.toString();
        return posBucketId === selectedId;
      });
    }

    // Apply sorting only if a column header was clicked (sortField is set)
    if (!sortField) {
      return filtered; // Return filtered positions without sorting
    }

    // Sort by the selected field
    return filtered.sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortField) {
        case POSITION_SORT_FIELD_SYMBOL:
          aValue = a.symbol || "";
          bValue = b.symbol || "";
          break;
        case POSITION_SORT_FIELD_LEVERAGE:
          // Convert leverage from satoshi to decimal for proper sorting
          try {
            aValue = a.leverage ? fromSatoshi(a.leverage) : 0;
            bValue = b.leverage ? fromSatoshi(b.leverage) : 0;
          } catch {
            aValue = 0;
            bValue = 0;
          }
          break;
        case POSITION_SORT_FIELD_SIZE:
          aValue = a.size || 0;
          bValue = b.size || 0;
          break;
        case POSITION_SORT_FIELD_LIQUIDATION_PRICE:
          aValue = a.liquidationPrice || 0;
          bValue = b.liquidationPrice || 0;
          break;
        case POSITION_SORT_FIELD_UNREALIZED_PNL:
          aValue = a.unrealizedPnL || 0;
          bValue = b.unrealizedPnL || 0;
          break;
        case POSITION_SORT_FIELD_ENTRY_PRICE:
          aValue = a.entryPrice || 0;
          bValue = b.entryPrice || 0;
          break;
        case POSITION_SORT_FIELD_MARGIN:
          aValue = a.margin || 0;
          bValue = b.margin || 0;
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === SORT_DIRECTION_ASC
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = Number(aValue);
      const numB = Number(bValue);

      return sortDirection === SORT_DIRECTION_ASC ? numA - numB : numB - numA;
    });
  }, [positions, sortField, sortDirection, selectedBucketId]);

  const handleSort = (field: PositionSortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === SORT_DIRECTION_ASC ? SORT_DIRECTION_DESC : SORT_DIRECTION_ASC);
    } else {
      setSortField(field);
      setSortDirection(SORT_DIRECTION_DESC);
    }
  };

  const handleClosePosition = (position: (typeof positions)[0]) => {
    // TODO: Implement close position functionality
    console.log("Close position:", position);
    toast.warning("Feature not implemented", {
      description: `Close position for ${position.symbol} - This functionality needs to be implemented`,
    });
  };

  if (loading) {
    return <LoadingMessage message={POSITION_LOADING_MESSAGE} />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="layout-container" style={{ height }}>
      {/* Header */}
      <LayoutHeader
        title={`${POSITION_TITLE_PREFIX} ${selectedBucketId ? `(Bucket ${selectedBucketId})` : ""}`}
      >
        <div
          onClick={() => {
            setViewMode(POSITION_VIEW_MODE_ALL);
            setSortField(null);
          }}
          className={`btn-tab ${viewMode === POSITION_VIEW_MODE_ALL ? "active" : ""}`}
        >
          {POSITION_TAB_LABEL_ALL}
        </div>
        <div
          onClick={() => {
            setViewMode(POSITION_VIEW_MODE_BY_BUCKET);
            setSortField(null);
          }}
          className={`btn-tab ${viewMode === POSITION_VIEW_MODE_BY_BUCKET ? "active" : ""}`}
        >
          <img src="/bucket-g.svg" />
          <span>{POSITION_TAB_LABEL_BY_BUCKET}</span>
        </div>
      </LayoutHeader>

      {/* Table */}
      <div className="table-container">
        {/* Table Header */}
        <div
          className="table-header"
          style={{
            gridTemplateColumns: POSITION_GRID_TEMPLATE_COLUMNS,
          }}
        >
          <TableHeaderCell
            field={POSITION_SORT_FIELD_SYMBOL}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Symbol
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_LEVERAGE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Leverage
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_SIZE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Size
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_LIQUIDATION_PRICE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Liq. Price
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_UNREALIZED_PNL}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            P/L
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_ENTRY_PRICE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Entry Price
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_MARGIN}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Margin
          </TableHeaderCell>
          <TableHeaderCellStatic center>Actions</TableHeaderCellStatic>
        </div>

        {/* Table Body */}
        {processedPositions.length === 0 ? (
          <EmptyMessage message={POSITION_EMPTY_MESSAGE} />
        ) : (
          processedPositions.map((position, index) => {
            const isLong = position.side === ORDER_SIDE_LONG;
            const pnlColor =
              (position.unrealizedPnL || 0) >= 0 ? POSITION_PNL_COLOR_POSITIVE : POSITION_PNL_COLOR_NEGATIVE;
            const leverageDisplay = formatLeverage(position.leverage);

            return (
              <div
                key={`position-${position.marketId}-${index}`}
                className={`table-row ${index % 2 === 0 ? "even" : ""}`}
                style={{
                  gridTemplateColumns: POSITION_GRID_TEMPLATE_COLUMNS,
                }}
              >
                {/* Symbol */}
                <div
                  className="table-cell primary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span>{position.symbol}</span>
                </div>

                {/* Leverage */}
                <div className="color-buy" style={{ fontWeight: 500 }}>
                  {isLong ? ORDER_SIDE_LONG : ORDER_SIDE_SHORT}{" "}
                  {leverageDisplay === POSITION_LEVERAGE_FALLBACK ? POSITION_LEVERAGE_FALLBACK : `${leverageDisplay}x`} @{" "}
                  {position.marginMode || MARGIN_MODE_CROSS}
                </div>

                {/* Size */}
                <div className="color-buy right">
                  {formatSize(position.size)}
                </div>

                {/* Liq. Price */}
                <div className="table-cell right">
                  {formatPrice(position.liquidationPrice)}
                </div>

                {/* P/L */}
                <div
                  className="table-cell right"
                  style={{ color: pnlColor, fontWeight: 500 }}
                >
                  {formatPnL(position.unrealizedPnL)}
                </div>

                {/* Entry Price */}
                <div className="table-cell right">
                  {formatPrice(position.entryPrice)}
                </div>

                {/* Margin */}
                <div className="table-cell right">
                  {formatMargin(position.margin)}
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CloseButton onClick={() => handleClosePosition(position)} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default React.memo(BucketPositions);
